import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Loader2, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { SettingToggle, SettingNumberInput, SettingTextarea } from '../ui/SettingsBox';

const SalonSettings: React.FC<{ organization: any, readOnly?: boolean }> = ({ organization, readOnly = false }) => {
    const { t } = useTranslation();
    const { updateOrganization } = useAuth();

    const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(false);
    // Cadenze promemoria dinamiche: lista di "ore prima" (es. [24,1]). Aggiungibili/rimovibili.
    const [reminderOffsets, setReminderOffsets] = useState<number[]>([24, 1]);
    const [isSavingReminders, setIsSavingReminders] = useState(false);
    const [newOffset, setNewOffset] = useState<string>('');
    const [constraintsEnabled, setConstraintsEnabled] = useState<boolean>(true);
    const [maxReportsLimit, setMaxReportsLimit] = useState<number | string>(3);
    const [maxBookingRange, setMaxBookingRange] = useState<number | string>('');
    const [isTogglingNotifications, setIsTogglingNotifications] = useState(false);
    const [isTogglingConstraints, setIsTogglingConstraints] = useState(false);
    const [isSavingReportsLimit, setIsSavingReportsLimit] = useState(false);
    const [isSavingBookingRange, setIsSavingBookingRange] = useState(false);
    const [reportPolicyText, setReportPolicyText] = useState<string>('');
    const [isSavingReportPolicy, setIsSavingReportPolicy] = useState(false);
    const [cancellationLimitHours, setCancellationLimitHours] = useState<number | string>(2);
    const [isSavingCancellationLimit, setIsSavingCancellationLimit] = useState(false);
    const [appBookingEnabled, setAppBookingEnabled] = useState<boolean>(true);
    const [isTogglingAppBooking, setIsTogglingAppBooking] = useState(false);
    const [dynamicSlotsEnabled, setDynamicSlotsEnabled] = useState<boolean>(false);
    const [isTogglingDynamicSlots, setIsTogglingDynamicSlots] = useState(false);
    const [multicolorEnabled, setMulticolorEnabled] = useState<boolean>(false);
    const [isTogglingMulticolor, setIsTogglingMulticolor] = useState(false);
    // Note tecniche: di default le vede tutto lo staff, il titolare può riservarle a sé
    const [technicalNotesOwnerOnly, setTechnicalNotesOwnerOnly] = useState<boolean>(false);
    const [isTogglingTechnicalNotes, setIsTogglingTechnicalNotes] = useState(false);

    useEffect(() => {
        if (organization) {
            setNotificationsEnabled(organization.notifications_enabled ?? false);
            setReminderOffsets(
                Array.isArray(organization.reminder_offsets_hours) && organization.reminder_offsets_hours.length >= 0
                    ? [...organization.reminder_offsets_hours].map(Number).filter((n) => !isNaN(n) && n > 0).sort((a, b) => b - a)
                    : [24, 1],
            );
            setConstraintsEnabled(organization.constraints_enabled !== false);
            setAppBookingEnabled(organization.app_booking_enabled !== false);
            setDynamicSlotsEnabled(organization.dynamic_slots_enabled === true);
            setMulticolorEnabled(organization.multicolor_appointments_enabled === true);
            setTechnicalNotesOwnerOnly(organization.technical_notes_visibility === 'owner_only');
            setMaxReportsLimit(organization.max_reports_limit ?? 3);
            setMaxBookingRange(organization.max_booking_range ?? '');
            setReportPolicyText(organization.report_policy_text || '');
            setCancellationLimitHours(organization.cancellation_limit_hours ?? 2);
        }
    }, [organization]);

    const handleToggleNotifications = async () => {
        if (readOnly || isTogglingNotifications) return;
        const newValue = !notificationsEnabled;
        setNotificationsEnabled(newValue);
        setIsTogglingNotifications(true);
        try {
            const res = await updateOrganization({ notifications_enabled: newValue });
            if (!res.success) {
                setNotificationsEnabled(!newValue);
                toast.error(t('common.save_error'));
            }
        } catch {
            setNotificationsEnabled(!newValue);
            toast.error(t('common.error'));
        } finally {
            setIsTogglingNotifications(false);
        }
    };

    // Salva la nuova lista di cadenze (ordinata desc) su Shops.reminder_offsets_hours
    const persistReminderOffsets = async (next: number[], previous: number[]) => {
        if (readOnly) return;
        setReminderOffsets(next);
        setIsSavingReminders(true);
        try {
            const res = await updateOrganization({ reminder_offsets_hours: next });
            if (!res.success) {
                setReminderOffsets(previous);
                toast.error(t('common.save_error'));
            }
        } catch {
            setReminderOffsets(previous);
            toast.error(t('common.error'));
        } finally {
            setIsSavingReminders(false);
        }
    };

    const handleAddReminderOffset = async () => {
        const h = Math.round(Number(newOffset));
        if (isNaN(h) || h <= 0) return;
        if (h > 720) { toast.error(t('salon.profile.notifications.reminder_max_error', 'Massimo 720 ore (30 giorni)')); return; }
        if (reminderOffsets.includes(h)) { toast.error(t('salon.profile.notifications.reminder_dup_error', 'Cadenza già presente')); return; }
        const next = [...reminderOffsets, h].sort((a, b) => b - a);
        setNewOffset('');
        await persistReminderOffsets(next, reminderOffsets);
    };

    const handleRemoveReminderOffset = async (h: number) => {
        const next = reminderOffsets.filter((o) => o !== h);
        await persistReminderOffsets(next, reminderOffsets);
    };

    // Etichetta leggibile per una cadenza in ore
    const reminderOffsetLabel = (h: number): string => {
        if (h === 1) return t('salon.profile.notifications.reminder_1h', '1 ora prima');
        if (h < 24) return t('salon.profile.notifications.reminder_hours', '{{h}} ore prima', { h });
        if (h % 24 === 0) {
            const days = h / 24;
            return days === 1
                ? t('salon.profile.notifications.reminder_1day', '24 ore prima (1 giorno)')
                : t('salon.profile.notifications.reminder_days', '{{h}} ore prima ({{d}} giorni)', { h, d: days });
        }
        return t('salon.profile.notifications.reminder_hours', '{{h}} ore prima', { h });
    };

    const handleToggleConstraints = async () => {
        if (readOnly || isTogglingConstraints) return;
        const newValue = !constraintsEnabled;
        setConstraintsEnabled(newValue);
        setIsTogglingConstraints(true);
        try {
            const res = await updateOrganization({ constraints_enabled: newValue });
            if (!res.success) {
                setConstraintsEnabled(!newValue);
                toast.error(t('common.save_error'));
            }
        } catch {
            setConstraintsEnabled(!newValue);
            toast.error(t('common.error'));
        } finally {
            setIsTogglingConstraints(false);
        }
    };

    const handleToggleAppBooking = async () => {
        if (readOnly || isTogglingAppBooking) return;
        const newValue = !appBookingEnabled;
        setAppBookingEnabled(newValue);
        setIsTogglingAppBooking(true);
        try {
            const res = await updateOrganization({ app_booking_enabled: newValue });
            if (!res.success) {
                setAppBookingEnabled(!newValue);
                toast.error(t('common.save_error'));
            }
        } catch {
            setAppBookingEnabled(!newValue);
            toast.error(t('common.error'));
        } finally {
            setIsTogglingAppBooking(false);
        }
    };

    const handleToggleDynamicSlots = async () => {
        if (readOnly || isTogglingDynamicSlots) return;
        const newValue = !dynamicSlotsEnabled;
        setDynamicSlotsEnabled(newValue);
        setIsTogglingDynamicSlots(true);
        try {
            const res = await updateOrganization({ dynamic_slots_enabled: newValue });
            if (!res.success) {
                setDynamicSlotsEnabled(!newValue);
                toast.error(t('common.save_error'));
            }
        } catch {
            setDynamicSlotsEnabled(!newValue);
            toast.error(t('common.error'));
        } finally {
            setIsTogglingDynamicSlots(false);
        }
    };

    const handleToggleMulticolor = async () => {
        if (readOnly || isTogglingMulticolor) return;
        const newValue = !multicolorEnabled;
        setMulticolorEnabled(newValue);
        setIsTogglingMulticolor(true);
        try {
            const res = await updateOrganization({ multicolor_appointments_enabled: newValue });
            if (!res.success) {
                setMulticolorEnabled(!newValue);
                toast.error(t('common.save_error'));
            }
        } catch {
            setMulticolorEnabled(!newValue);
            toast.error(t('common.error'));
        } finally {
            setIsTogglingMulticolor(false);
        }
    };

    const handleToggleTechnicalNotesPrivacy = async () => {
        if (readOnly || isTogglingTechnicalNotes) return;
        const newValue = !technicalNotesOwnerOnly;
        setTechnicalNotesOwnerOnly(newValue);
        setIsTogglingTechnicalNotes(true);
        try {
            const res = await updateOrganization({
                technical_notes_visibility: newValue ? 'owner_only' : 'all',
            });
            if (!res.success) {
                setTechnicalNotesOwnerOnly(!newValue);
                toast.error(t('common.save_error'));
            }
        } catch {
            setTechnicalNotesOwnerOnly(!newValue);
            toast.error(t('common.error'));
        } finally {
            setIsTogglingTechnicalNotes(false);
        }
    };

    const handleSaveMaxBookingRange = async () => {
        if (readOnly || isSavingBookingRange) return;

        const limitToSave = typeof maxBookingRange === 'string' ? parseInt(maxBookingRange, 10) : maxBookingRange;
        if (isNaN(limitToSave) || limitToSave < 1) {
            setMaxBookingRange(organization?.max_booking_range || 12);
            return;
        }

        if (limitToSave === organization?.max_booking_range) return;

        setIsSavingBookingRange(true);
        try {
            const res = await updateOrganization({ max_booking_range: limitToSave });
            if (res.success) {
                toast.success(t('common.save_success'));
            } else {
                setMaxBookingRange(organization?.max_booking_range ?? '');
                toast.error(t('common.save_error'));
            }
        } catch {
            setMaxBookingRange(organization?.max_booking_range ?? '');
            toast.error(t('common.error'));
        } finally {
            setIsSavingBookingRange(false);
        }
    };

    const handleSaveMaxReportsLimit = async () => {
        if (readOnly || isSavingReportsLimit) return;

        const limitToSave = typeof maxReportsLimit === 'string' ? parseInt(maxReportsLimit, 10) : maxReportsLimit;
        if (isNaN(limitToSave) || limitToSave < 1) {
            setMaxReportsLimit(organization?.max_reports_limit ?? 3);
            return;
        }

        if (limitToSave === organization?.max_reports_limit) return;

        setIsSavingReportsLimit(true);
        try {
            const res = await updateOrganization({ max_reports_limit: limitToSave });
            if (res.success) {
                toast.success(t('common.save_success'));
            } else {
                setMaxReportsLimit(organization?.max_reports_limit ?? 3);
                toast.error(t('common.save_error'));
            }
        } catch {
            setMaxReportsLimit(organization?.max_reports_limit ?? 3);
            toast.error(t('common.error'));
        } finally {
            setIsSavingReportsLimit(false);
        }
    };

    const handleSaveCancellationLimit = async () => {
        if (readOnly || isSavingCancellationLimit) return;

        const limitToSave = typeof cancellationLimitHours === 'string' ? parseInt(cancellationLimitHours, 10) : cancellationLimitHours;
        if (isNaN(limitToSave) || limitToSave < 0) {
            setCancellationLimitHours(organization?.cancellation_limit_hours ?? 2);
            return;
        }

        if (limitToSave === organization?.cancellation_limit_hours) return;

        setIsSavingCancellationLimit(true);
        try {
            const res = await updateOrganization({ cancellation_limit_hours: limitToSave });
            if (res.success) {
                toast.success(t('common.save_success'));
            } else {
                setCancellationLimitHours(organization?.cancellation_limit_hours ?? 2);
                toast.error(t('common.save_error'));
            }
        } catch {
            setCancellationLimitHours(organization?.cancellation_limit_hours ?? 2);
            toast.error(t('common.error'));
        } finally {
            setIsSavingCancellationLimit(false);
        }
    };

    const handleSaveReportPolicy = async () => {
        if (readOnly || isSavingReportPolicy) return;

        const textToSave = reportPolicyText.trim();
        if (textToSave === (organization?.report_policy_text || '')) return;

        setIsSavingReportPolicy(true);
        try {
            const res = await updateOrganization({ report_policy_text: textToSave });
            if (res.success) {
                toast.success(t('common.save_success'));
            } else {
                setReportPolicyText(organization?.report_policy_text || '');
                toast.error(t('common.save_error'));
            }
        } catch {
            setReportPolicyText(organization?.report_policy_text || '');
            toast.error(t('common.error'));
        } finally {
            setIsSavingReportPolicy(false);
        }
    };

    return (
        <motion.div variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }} className="space-y-6">
            {/* IMPOSTAZIONI SALONE */}
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-6">
                    <Settings size={20} className="text-clip-500" /> {t('salon.profile.notifications.settings_title', 'Impostazioni Salone')}
                </h3>
                <div className="space-y-4">
                    <SettingToggle
                        label={t('salon.profile.notifications.appointments_label')}
                        description={t('salon.profile.notifications.appointments_desc')}
                        checked={notificationsEnabled}
                        onChange={handleToggleNotifications}
                        disabled={isTogglingNotifications}
                        readOnly={readOnly}
                    />
                    {/* Cadenze promemoria (notifica push + in-app al cliente): dinamiche, attive solo se le notifiche sono abilitate.
                        Stesso box degli altri (grigio arrotondato): riga 1 etichetta a sx + campo/Aggiungi a dx, tag nella riga sottostante. */}
                    {notificationsEnabled && (
                        <div className={`p-4 bg-gray-50 rounded-2xl border border-gray-100 ${readOnly ? 'opacity-60' : ''}`}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 mr-4">
                                    <p className="font-semibold text-gray-800 text-sm">
                                        {t('salon.profile.notifications.reminders_title', 'Promemoria al cliente (push)')}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        {t('salon.profile.notifications.reminders_desc', 'Notifiche inviate al cliente prima dell’appuntamento. Aggiungi o rimuovi quante cadenze vuoi.')}
                                    </p>
                                </div>
                                {!readOnly && (
                                    <div className="flex items-stretch gap-2 flex-shrink-0">
                                        <div className="relative">
                                            <input
                                                type="number"
                                                min="1"
                                                max="720"
                                                step="1"
                                                value={newOffset}
                                                onChange={(e) => setNewOffset(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddReminderOffset(); } }}
                                                disabled={isSavingReminders}
                                                className="w-28 h-full pl-3 pr-9 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none disabled:opacity-60"
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                                                {t('salon.profile.notifications.reminder_hours_unit', 'ore')}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleAddReminderOffset}
                                            disabled={isSavingReminders || !newOffset}
                                            className="flex items-center justify-center gap-1 self-stretch px-3 rounded-lg bg-clip-500 hover:bg-clip-600 text-white text-sm font-bold shadow-sm shadow-clip-200 transition-all active:scale-95 disabled:opacity-50"
                                        >
                                            {isSavingReminders ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                            {t('common.add', 'Aggiungi')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-wrap gap-2 mt-3">
                                {reminderOffsets.length === 0 && (
                                    <span className="text-xs text-gray-400 italic">
                                        {t('salon.profile.notifications.reminders_empty', 'Nessun promemoria: il cliente non riceverà notifiche.')}
                                    </span>
                                )}
                                {reminderOffsets.map((h) => (
                                    <span key={h} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-lg bg-clip-50 border border-clip-200 text-clip-700 text-xs font-bold">
                                        {reminderOffsetLabel(h)}
                                        {!readOnly && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveReminderOffset(h)}
                                                disabled={isSavingReminders}
                                                title={t('common.remove', 'Rimuovi')}
                                                className="w-5 h-5 flex items-center justify-center rounded-md hover:bg-clip-100 text-clip-500 disabled:opacity-50"
                                            >
                                                <X size={13} />
                                            </button>
                                        )}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    <SettingToggle
                        label={t('salon.profile.notifications.settings_constraints_label', 'Vincoli Calendario')}
                        description={t('salon.profile.notifications.settings_constraints_desc', 'Se attivo, rispetta pause, orari e ferie nel calendario')}
                        checked={constraintsEnabled}
                        onChange={handleToggleConstraints}
                        disabled={isTogglingConstraints}
                        readOnly={readOnly}
                    />
                    <SettingToggle
                        label={t('salon.profile.notifications.app_booking_label', 'Prenotazioni in App')}
                        description={t('salon.profile.notifications.app_booking_desc', 'Abilita o disabilita le prenotazioni tramite l’applicazione Clip')}
                        checked={appBookingEnabled}
                        onChange={handleToggleAppBooking}
                        disabled={isTogglingAppBooking}
                        readOnly={readOnly}
                    />
                    <SettingToggle
                        label={t('salon.profile.notifications.dynamic_slots_label')}
                        description={t('salon.profile.notifications.dynamic_slots_desc')}
                        checked={dynamicSlotsEnabled}
                        onChange={handleToggleDynamicSlots}
                        disabled={isTogglingDynamicSlots}
                        readOnly={readOnly}
                    />
                    <SettingToggle
                        label={t('salon.profile.notifications.multicolor_label', 'Appuntamenti multicolore')}
                        description={t('salon.profile.notifications.multicolor_desc', 'Colora ogni appuntamento con una tinta diversa a rotazione, invece del colore unico arancione')}
                        checked={multicolorEnabled}
                        onChange={handleToggleMulticolor}
                        disabled={isTogglingMulticolor}
                        readOnly={readOnly}
                    />
                    <SettingToggle
                        label={t('salon.profile.notifications.technical_notes_label', 'Note tecniche riservate al titolare')}
                        description={t('salon.profile.notifications.technical_notes_desc', 'Se attivo, le note tecniche dei clienti (formule colore e simili) sono visibili solo a te e non ai collaboratori')}
                        checked={technicalNotesOwnerOnly}
                        onChange={handleToggleTechnicalNotesPrivacy}
                        disabled={isTogglingTechnicalNotes}
                        readOnly={readOnly}
                    />
                    <SettingNumberInput
                        label={t('salon.profile.notifications.max_booking_range_label')}
                        description={t('salon.profile.notifications.max_booking_range_desc')}
                        value={maxBookingRange}
                        min={1}
                        onChange={(e) => setMaxBookingRange(e.target.value === '' ? '' : parseInt(e.target.value))}
                        onBlur={handleSaveMaxBookingRange}
                        disabled={isSavingBookingRange}
                        readOnly={readOnly}
                        isSaving={isSavingBookingRange}
                    />
                    <SettingNumberInput
                        label={t('salon.profile.notifications.max_reports_label', 'Limite segnalazioni clienti')}
                        description={t('salon.profile.notifications.max_reports_desc', 'Numero massimo di segnalazioni prima che il cliente venga bloccato')}
                        value={maxReportsLimit}
                        min={1}
                        max={10}
                        onChange={(e) => setMaxReportsLimit(e.target.value === '' ? '' : parseInt(e.target.value))}
                        onBlur={handleSaveMaxReportsLimit}
                        disabled={isSavingReportsLimit}
                        readOnly={readOnly}
                        isSaving={isSavingReportsLimit}
                    />
                    <SettingNumberInput
                        label={t('salon.profile.notifications.cancellation_limit_label', 'Limite ore disdetta')}
                        description={t('salon.profile.notifications.cancellation_limit_desc', 'Fino a quante ore prima è possibile disdire un appuntamento')}
                        value={cancellationLimitHours}
                        min={0}
                        onChange={(e) => setCancellationLimitHours(e.target.value === '' ? '' : parseInt(e.target.value))}
                        onBlur={handleSaveCancellationLimit}
                        disabled={isSavingCancellationLimit}
                        readOnly={readOnly}
                        isSaving={isSavingCancellationLimit}
                    />
                    <SettingTextarea
                        label={t('salon.profile.notifications.report_policy_label', 'Messaggio al cliente in caso di segnalazione')}
                        description={t('salon.profile.notifications.report_policy_desc', "Il testo che il cliente riceve se viene segnalato. Meglio un tono umano e gentile, non legale.")}
                        value={reportPolicyText}
                        placeholder={t('salon.profile.notifications.report_policy_placeholder', "Facoltativo. Es: \"Ci dispiace che non sia andata come previsto! Se non puoi venire, avvisaci in tempo: la disdetta è gratuita fino a 24h prima.\"")}
                        onChange={(e) => setReportPolicyText(e.target.value)}
                        onBlur={handleSaveReportPolicy}
                        disabled={isSavingReportPolicy}
                        readOnly={readOnly}
                        isSaving={isSavingReportPolicy}
                    />
                </div>
            </div>
        </motion.div>
    );
};

export default SalonSettings;
